import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

const ACCORDION_ANIMATION_TIME = 350;

export default class AccordionContent extends React.PureComponent {
    static propTypes = {
        /** id used to construct the aria relationship between the heading and
         * content. It's only necessary to provide an id if there's more than
         * one Accordion on the page.  */
        id: PropTypes.string,

        /** Contents of the panel */
        children: PropTypes.node,

        /** If the contents are expanded */
        isExpanded: PropTypes.bool,

        /** Aligns the contents with the heading text instead of the icon. */
        hasIndentedBody: PropTypes.bool,

        /** Additional class applied to contents wrapper */
        className: PropTypes.string
    };

    static defaultProps = {
        hasIndentedBody: true
    };

    constructor(props) {
        super(props);

        this.outerDivRef = React.createRef();
        this.innerDivRef = React.createRef();
    }

    componentDidMount() {
        const { isExpanded } = this.props;

        // Safety net for tests since some testing methods could fail for lack of DOM goodness
        if (this.outerDivRef.current && this.outerDivRef.current.style) {
            this.outerDivRef.current.style.maxHeight = isExpanded ? 'none' : '0';
            this.innerDivRef.current.style.display = isExpanded ? 'block' : 'none';
        }
    }

    componentDidUpdate(prevProps) {
        const { isExpanded } = this.props;

        // Collapsed to expanded
        if (isExpanded && !prevProps.isExpanded) {
            clearTimeout(this.timeout);
            this.innerDivRef.current.style.display = 'block';
            // restore SR visibility
            this.innerDivRef.current.setAttribute('aria-hidden', 'false');

            const contentHeight = this.innerDivRef.current.clientHeight
                ? this.innerDivRef.current.clientHeight
                : 'none';

            this.outerDivRef.current.style.maxHeight = `${contentHeight}px`;

            // After the animation, remove the maxHeight so the content can
            // respond to window size changes
            this.timeout = setTimeout(() => {
                this.outerDivRef.current.style.maxHeight = 'none';
            }, ACCORDION_ANIMATION_TIME);
        }

        // Expanded to collapsed
        if (!isExpanded && prevProps.isExpanded) {
            clearTimeout(this.timeout);
            // content is still in the DOM while animating out, so
            // prevent screen readers from accessing the contents if they
            // navigate during animation.
            this.innerDivRef.current.setAttribute('aria-hidden', 'true');

            const contentHeight = this.innerDivRef.current.clientHeight
                ? this.innerDivRef.current.clientHeight
                : 'none';

            this.outerDivRef.current.style.maxHeight = `${contentHeight}px`;

            setTimeout(() => {
                this.outerDivRef.current.style.maxHeight = '0';
            }, 0);

            // After the collapse animation, hide the content
            this.timeout = setTimeout(() => {
                this.innerDivRef.current.style.display = 'none';
            }, ACCORDION_ANIMATION_TIME);
        }
    }

    componentWillUnmount() {
        clearTimeout(this.timeout);
    }

    render() {
        const {
            children,
            hasIndentedBody,
            className,
            id
        } = this.props;

        // The outer div holds the styles and max-height needed for the
        // animation; its styling should not be adjusted for custom cosmetic
        // reasons

        return (
            <div
                ref={this.outerDivRef}
                id={id}
                className={cx('sarsa-accordion-content-outer', className)}>
                <div
                    data-component="AccordionContent"
                    ref={this.innerDivRef}
                    role="region"
                    aria-labelledby={`${id}-heading`}
                    style={{ boxSizing: 'border-box !important' }}
                    className={cx('sarsa-accordion-content-inner', {
                        'has-indented-body': hasIndentedBody
                    })}>
                    {children}
                </div>
            </div>
        );
    }
}